import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowUpRight, Play, Quote, Volume2 } from "lucide-react";
import { Link } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import reviewsDoodles from "@/assets/reviews-doodles.svg";
import { parseVideoLink } from "../lib/videoPreview";
import "./Reviews.css";

/* ============================================================
   ADD YOUR 10 VIDEO LINKS HERE
   - Paste Google Drive share links (file must be shared as
     "Anyone with the link can view"), or direct .mp4 / .webm URLs.
   - The circle cover (thumbnail) is taken automatically from the link.
   - Leave "" for a video you don't have yet.
   ============================================================ */
const REVIEW_VIDEO_LINKS = [
  "https://drive.google.com/file/d/1BgkzFrAkUxEUtI0l7nUWDo2GvDZKcxFW/view?usp=sharing", // 1  e.g. "https://drive.google.com/file/d/FILE_ID/view?usp=sharing"
  "https://drive.google.com/file/d/1OPPvaScRzrontbS2leADx-HVOTTyCh-a/view?usp=sharing", // 1  e.g. "https://drive.google.com/file/d/FILE_ID/view?usp=sharing"
  "https://drive.google.com/file/d/1L5kp4-qbmdPoLrLfejr0rfZ7zyRFa6eN/view?usp=sharing", // 2
  "https://drive.google.com/file/d/14Qq7PN3LYVPZpgewfoQDeBVN0A6GkGih/view?usp=sharing", // 3
  "https://drive.google.com/file/d/1EguBzS6s3qcQWLeFftbH2ifeMZgcw3H_/view?usp=sharing", // 4
  "https://drive.google.com/file/d/1cgc0p4v7wzmDtimNaRgOIiL3Fwt3hF8s/view?usp=sharing", // 5
  "https://drive.google.com/file/d/1nBlv08FzZvEsN5NKwsjKEtdBif-i9uNd/view?usp=sharing", // 6
  "https://drive.google.com/file/d/1EguBzS6s3qcQWLeFftbH2ifeMZgcw3H_/view?usp=sharing", // 7
  "https://drive.google.com/file/d/1SeyEbr2Q4UrEMMB-2sW9WayRCkpwtqx5/view?usp=sharing", // 8
  "https://drive.google.com/file/d/1OPPvaScRzrontbS2leADx-HVOTTyCh-a/view?usp=sharing", // 9
  "https://drive.google.com/file/d/1rnhfYKHcZQR4S3iUbTIclmka_cMcmi_h/view?usp=sharing", // 10
];

const REVIEW_QUOTES = [
  "The biggest difference for me is having a plan before the market moves. I am much more patient with my entries now.",
  "The explanations make the setups much easier to follow. I have stopped chasing every candle and started waiting for confirmation.",
  "I joined to bring more discipline to my trading. The daily structure keeps me focused on risk instead of reacting emotionally.",
  "The room helped me understand why waiting for the right level matters. I am finally trading with more patience and less guesswork.",
  "The clear levels and updates made this setup much easier to manage. I appreciate how focused the guidance stays.",
  "I like that the calls come with a plan instead of noise. It has made my trading routine feel much more structured.",
  "The explanations around entries and targets are simple to follow. That clarity has helped me stay consistent with my process.",
  "Seeing the plan play out gave me more confidence in following my rules. The focus on discipline is what I value most.",
  "Risk management finally clicked for me here. I size my positions properly now instead of guessing.",
  "Every update comes with a reason, not just a level. That is what made me trust the process.",
  "I used to overtrade. Having a clear plan each day has cut my random entries a lot.",
  "The community keeps me accountable. I follow my rules more consistently than I ever did alone.",
];

/* ============================================================
   LAYOUT SETTINGS
   Everything is in "grid units": the grid is W units wide and H units
   tall, and a circle of size 12 is 12% of the grid width. Because sizes
   are percentages, the whole layout scales smoothly with the screen.
   Change COUNTS or the size ranges to tune the look.
   ============================================================ */
const COUNTS = { large: 5, medium: 15, small: 25 }; // total 45, 10 of them are videos
const VIDEO_COUNT = 10;

const LAYOUTS = {
  desktop: { W: 100, H: 84, gap: 1.2, margin: 2.5, small: [7, 9], medium: [10, 12.5], large: [13.5, 16] },
  mobile: { W: 100, H: 280, gap: 1.8, margin: 3, small: [14, 17], medium: [18, 21], large: [23, 27] },
};

const rand = (min, max) => min + Math.random() * (max - min);
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};


/* Random but well-spread placement:
   - a few big circles, a good number of medium ones, many small ones
   - biggest circles are placed first so they always fit
   - for every circle we try many random spots and pick one of the
     emptiest ones, so circles spread out evenly without looking like a grid
   - if the area is crowded, the circle shrinks a little until it fits */
const placeCircles = (cfg) => {
  const sizes = [];
  Object.entries(COUNTS).forEach(([tier, count]) => {
    for (let i = 0; i < count; i++) sizes.push({ tier, size: rand(...cfg[tier]) });
  });
  sizes.sort((a, b) => b.size - a.size);

  const placed = [];
  const floor = cfg.small[0] * 0.8;

  sizes.forEach((item) => {
    let size = item.size;
    let chosen = null;
    let lastBest = null;

    for (let round = 0; round < 16 && !chosen; round++) {
      const r = size / 2;
      const candidates = [];

      for (let k = 0; k < 60; k++) {
        const x = rand(r + cfg.margin, cfg.W - r - cfg.margin);
        const y = rand(r + cfg.margin, cfg.H - r - cfg.margin);
        let clear = 50;
        for (const p of placed) {
          clear = Math.min(clear, Math.hypot(p.x - x, p.y - y) - p.r - r);
        }
        candidates.push({ x, y, clear });
      }

      candidates.sort((a, b) => b.clear - a.clear);
      lastBest = { ...candidates[0], size };
      const fits = candidates.filter((c) => c.clear >= cfg.gap);

      if (fits.length) {
        // pick randomly among the emptiest few spots: even but organic
        const pick = fits[Math.floor(Math.random() * Math.min(4, fits.length))];
        chosen = { x: pick.x, y: pick.y, size };
      } else {
        size = Math.max(floor, size * 0.95);
      }
    }

    const final = chosen || lastBest;
    placed.push({ x: final.x, y: final.y, size: final.size, r: final.size / 2, tier: item.tier });
  });

  return placed;
};

const buildLayout = (narrow) => {
  const cfg = narrow ? LAYOUTS.mobile : LAYOUTS.desktop;
  const placed = placeCircles(cfg);

  // Videos: all large circles + some medium ones, so covers are easy to see
  const large = placed.filter((p) => p.tier === "large");
  const medium = shuffle(placed.filter((p) => p.tier === "medium"));
  const videoSet = new Set([...large, ...medium.slice(0, Math.max(0, VIDEO_COUNT - large.length))]);

  // Random DOM order, so IDs / colours / link order are not sorted by size
  const order = shuffle(placed);
  let videoIndex = 0;
  let textIndex = 0;

  const slots = order.map((p, index) => {
    const isVideo = videoSet.has(p);
    const media = isVideo ? parseVideoLink(REVIEW_VIDEO_LINKS[videoIndex++]) : null;
    return {
      id: index + 1,
      label: `Member ${String(index + 1).padStart(2, "0")}`,
      type: isVideo ? "video" : "message",
      tier: p.tier,
      compact: p.tier === "small",
      size: p.size,
      x: (p.x / cfg.W) * 100,
      y: (p.y / cfg.H) * 100,
      videoUrl: media?.videoUrl || "",
      coverUrl: media?.coverUrl || "",
      isFile: media?.isFile || false,
      invalidLink: media?.invalidLink || false,
      message: isVideo ? "" : REVIEW_QUOTES[textIndex++ % REVIEW_QUOTES.length],
    };
  });

  return { slots, ratio: `${cfg.W} / ${cfg.H}` };
};

const useIsNarrow = () => {
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.innerWidth < 560);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 559px)");
    const update = () => setNarrow(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return narrow;
};

const SpotlightVideo = ({ slot }) =>
  slot.isFile ? (
    <video
      key={slot.id}
      src={slot.videoUrl}
      className="review-orbit__video review-orbit__video--file"
      autoPlay
      loop
      muted
      controls
      playsInline
      data-testid={`review-spotlight-video-${slot.id}`}
    />
  ) : (
    <iframe
      key={slot.id}
      src={slot.videoUrl}
      title={`${slot.label} review video`}
      allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
      allowFullScreen
      className="review-orbit__video"
      data-testid={`review-spotlight-video-${slot.id}`}
    />
  );

const VideoCover = ({ slot }) => {
  const [unavailable, setUnavailable] = useState(false);

  if (unavailable) {
    return <span className="review-orbit__preview-fallback">PREVIEW UNAVAILABLE</span>;
  }

  return (
    <>
      <img
        src={slot.coverUrl}
        alt={`${slot.label} video preview`}
        loading="lazy"
        referrerPolicy="no-referrer"
        className="review-orbit__cover"
        onError={() => setUnavailable(true)}
      />
      <span className="review-orbit__cover-play"><Play className="h-4 w-4 fill-current" /></span>
    </>
  );
};

const Reviews = () => {
  const narrow = useIsNarrow();
  const { slots: reviewSlots, ratio } = useMemo(() => buildLayout(narrow), [narrow]);
  const [hoveredSlot, setHoveredSlot] = useState(null);
  const [touchSlot, setTouchSlot] = useState(null);
  const [focusedSlot, setFocusedSlot] = useState(null);
  const [pointer, setPointer] = useState({ x: 50, y: 50 });
  const hoverTimer = useRef(null);
  const activeSlot = touchSlot || hoveredSlot;
  const spotlight = reviewSlots.find((s) => s.id === activeSlot);

  const handlePointerEnter = (event, slotId) => {
    if (event.pointerType === "mouse") {
      clearTimeout(hoverTimer.current);
      setTouchSlot(null);
      hoverTimer.current = setTimeout(() => setHoveredSlot(slotId), 90);
    }
  };

  const handlePointerLeave = (event, slotId) => {
    if (event.pointerType === "mouse") {
      clearTimeout(hoverTimer.current);
      setHoveredSlot((current) => (current === slotId ? null : current));
    }
  };

  const handlePointerDown = (event, slotId) => {
    if (event.pointerType === "touch") {
      setHoveredSlot(null);
      setTouchSlot((current) => (current === slotId ? null : slotId));
    }
  };

  const handleKeyboardActivation = (event, slotId) => {
    if (event.detail === 0) {
      setTouchSlot((current) => (current === slotId ? null : slotId));
    }
  };

  const handleGridPointerMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    setPointer({ x, y });
  };

  return (
    <main
      className="reviews-page min-h-screen"
      style={{ "--reviews-doodles": `url(${reviewsDoodles})` }}
      data-testid="reviews-page"
    >
      <div className="reviews-page__content">
        <header className="reviews-header">
          <Link to="/" className="reviews-brand" aria-label="50 CR CLUB home">
            <BrandLogo variant="mark" className="h-9 w-9 rounded-lg" />
            <span>50 CR <span>CLUB</span></span>
          </Link>
          <nav className="reviews-nav" aria-label="Reviews navigation">
            <Link to="/" className="reviews-nav__link"><ArrowLeft className="h-3.5 w-3.5" /> The club</Link>
            <Link to="/checkout" className="reviews-nav__join">Join the room <ArrowUpRight className="h-3.5 w-3.5" /></Link>
          </nav>
        </header>

        <section className="reviews-intro">
          <p className="reviews-eyebrow"><span /> 50 CR CLUB / MEMBER VOICES</p>
          <div className="reviews-title-row">
            <div>
              <h1>Reviews from <span>the room.</span></h1>
              <p className="reviews-intro__copy">Voices, moments, and notes from people in the room.</p>
            </div>
            <p className="reviews-count"><strong>{reviewSlots.length}</strong><span>MEMBER VOICES</span></p>
          </div>
        </section>

        <section
          className="reviews-grid"
          aria-label="Member review circles"
          data-testid="reviews-grid"
          onPointerMove={handleGridPointerMove}
          style={{
            aspectRatio: ratio,
            "--pointer-x": `${pointer.x}%`,
            "--pointer-y": `${pointer.y}%`,
          }}
        >
          {reviewSlots.map((slot) => {
            const isActive = activeSlot === slot.id;
            const isHighlighted = isActive || focusedSlot === slot.id;
            const hasVideo = slot.type === "video";
            const distance = Math.hypot(slot.x - pointer.x, slot.y - pointer.y);
            const intensity = Math.max(0, 1 - distance / 34);
            return (
              <button
                key={slot.id}
                type="button"
                className={[
                  "review-orbit",
                  `review-orbit--${slot.tier}`,
                  slot.compact ? "review-orbit--compact" : "",
                  isHighlighted ? "review-orbit--active" : "",
                  isActive ? "review-orbit--spotlit" : "",
                ].join(" ")}
                aria-label={hasVideo ? `${slot.label} video review` : `${slot.label} written review`}
                aria-pressed={isActive}
                title={slot.label}
                data-testid={`review-slot-${slot.id}`}
                style={{
                  "--orbit-size": `${slot.size}%`,
                  "--slot-left": `${slot.x}%`,
                  "--slot-top": `${slot.y}%`,
                  "--float-x": `${(pointer.x - slot.x) * 0.18}px`,
                  "--float-y": `${(pointer.y - slot.y) * 0.18}px`,
                  "--pulse-strength": `${0.45 + intensity * 0.9}`,
                }}
                onPointerEnter={(event) => handlePointerEnter(event, slot.id)}
                onPointerLeave={(event) => handlePointerLeave(event, slot.id)}
                onPointerDown={(event) => handlePointerDown(event, slot.id)}
                onFocus={() => setFocusedSlot(slot.id)}
                onBlur={() => setFocusedSlot((current) => (current === slot.id ? null : current))}
                onClick={(event) => handleKeyboardActivation(event, slot.id)}
              >
                {hasVideo && slot.coverUrl ? (
                  <VideoCover slot={slot} />
                ) : hasVideo && slot.isFile ? (
                  /* Direct mp4: first frame used as the cover */
                  <>
                    <video
                      src={`${slot.videoUrl}#t=0.5`}
                      className="review-orbit__video review-orbit__video--file"
                      muted
                      playsInline
                      preload="metadata"
                    />
                    <span className="review-orbit__cover-play"><Play className="h-4 w-4 fill-current" /></span>
                  </>
                ) : (
                  <span className={`review-orbit__placeholder review-orbit__placeholder--${slot.type}`}>
                    {hasVideo ? (
                      <>
                        <span className="review-orbit__number">{String(slot.id).padStart(2, "0")}</span>
                        <span className="review-orbit__play"><Volume2 className="h-4 w-4" /></span>
                        <span className="review-orbit__label">
                          {slot.invalidLink ? "PREVIEW UNAVAILABLE" : "VIDEO LINK NEEDED"}
                        </span>
                      </>
                    ) : (
                      <>
                        <Quote className="review-orbit__quote-icon" />
                        <span className="review-orbit__message">“{slot.message}”</span>
                        <span className="review-orbit__label">{slot.label}</span>
                      </>
                    )}
                  </span>
                )}
              </button>
            );
          })}
        </section>

        <footer className="reviews-footer">
          <span>TRADE WITH A PLAN. MANAGE YOUR RISK.</span>
          <Link to="/" className="reviews-footer__home">Back to 50 CR CLUB <ArrowUpRight className="h-3 w-3" /></Link>
        </footer>
      </div>

      {spotlight && (
        <div
          className={`review-spotlight ${touchSlot ? "review-spotlight--touch" : ""}`}
          onClick={() => setTouchSlot(null)}
          data-testid="review-spotlight"
        >
          <div className={`review-spotlight__card review-spotlight__card--${spotlight.type}`}>
            {spotlight.type === "video" ? (
              spotlight.videoUrl ? (
                <SpotlightVideo slot={spotlight} />
              ) : (
                <div className="review-spotlight__empty">
                  <Volume2 className="h-8 w-8" />
                  <span>VIDEO LINK NEEDED</span>
                </div>
              )
            ) : (
              <div className="review-spotlight__text">
                <Quote className="review-spotlight__quote-icon" />
                <p>“{spotlight.message}”</p>
                <span>{spotlight.label}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
};

export default Reviews;
